import { lumi } from "../lib/lumi"
import { useState, useEffect } from "react"
import { useAuth } from "../hooks/useAuth"

type TimeEntry = {
  _id: string
  userId: string
  userName?: string
  weekStart: string
  weekEnd: string
  status: string
  createdAt: string
}

export function TimeEntrySubmissionStatus() {
  const { isAdmin } = useAuth()
  const [loading, setLoading] = useState(false)
  const [entries, setEntries] = useState<TimeEntry[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  useEffect(() => {
    loadData()
  }, [])

  // 주차 계산 함수
  const getWeekOfMonth = (dateStr: string) => {
    const date = new Date(dateStr)
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const dayOfWeek = firstDay.getDay()
    const dayOfMonth = date.getDate()
    return Math.ceil((dayOfMonth + dayOfWeek) / 7)
  }

  // 투입주간 포맷 함수
  const formatWeekDisplay = (weekStart: string) => {
    const date = new Date(weekStart)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const weekNum = getWeekOfMonth(weekStart)
    return `${year}/${month}/${day} (${weekNum}주차)`
  }

  const loadData = async () => {
    setLoading(true)
    try {
      // time_entries2에서 모든 투입시간 데이터 조회
      const result = await lumi.entities.time_entries2.list({
        sort: { createdAt: -1 }
      })

      const allEntries = result?.list || []
      
      // 사용자 프로필 조회하여 userName 매핑
      const userResult = await lumi.entities.user_profiles3.list({
        filter: { teamName: { $ne: "CEO" } }
      })
      const users = userResult?.list || []
      const userMap = new Map(users.map((u: any) => [u.userId, u.userName]))

      // userName 추가 및 주간별로 그룹화
      const entriesWithNames = allEntries.map((entry: any) => ({
        ...entry,
        userName: userMap.get(entry.userId) || entry.userId
      }))

      // userId와 weekStart로 그룹화
      const groupedMap = new Map<string, TimeEntry>()
      
      entriesWithNames.forEach((entry: any) => {
        const key = `${entry.userId}_${entry.weekStart}`
        
        if (!groupedMap.has(key)) {
          groupedMap.set(key, entry)
        } else {
          // 이미 있는 경우, status가 submitted인지 확인
          const existing = groupedMap.get(key)!
          if (entry.status === "submitted") {
            groupedMap.set(key, { ...existing, status: "submitted" })
          }
        }
      })

      const groupedEntries = Array.from(groupedMap.values())
      setEntries(groupedEntries)
    } catch (error) {
      console.error("데이터 로드 실패:", error)
    } finally {
      setLoading(false)
    }
  }

  // 반려 처리 함수
  const handleReject = async (userId: string, weekStart: string, userName: string) => {
    if (!confirm(`${userName}님의 ${formatWeekDisplay(weekStart)} 투입시간을 반려하시겠습니까?\n사용자가 다시 수정할 수 있습니다.`)) {
      return
    }

    try {
      // 해당 userId와 weekStart를 가진 모든 레코드 조회
      const result = await lumi.entities.time_entries2.list({
        filter: {
          userId: userId,
          weekStart: weekStart
        }
      })

      const recordsToUpdate = result?.list || []
      
      // 모든 레코드를 draft로 업데이트
      for (const record of recordsToUpdate) {
        await lumi.entities.time_entries2.update(record._id, {
          status: "draft"
        })
      }

      alert("반려 처리가 완료되었습니다.")
      loadData() // 데이터 새로고침
    } catch (error) {
      console.error("반려 처리 실패:", error)
      alert("반려 처리 중 오류가 발생했습니다.")
    }
  }

  // 페이지네이션
  const totalPages = Math.ceil(entries.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentEntries = entries.slice(startIndex, endIndex)

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <p className="text-gray-600">관리자 권한이 필요합니다.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">투입시간 제출 현황</h2>
            <button
              onClick={loadData}
              disabled={loading}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
            >
              {loading ? "로딩 중..." : "새로고침"}
            </button>
          </div>

          {/* 목록 */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              <p className="mt-4 text-gray-600">데이터를 불러오는 중...</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              투입시간 데이터가 없습니다.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        사용자이름
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        투입주간
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        제출여부
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        관리
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentEntries.map((entry) => (
                      <tr key={entry._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {entry.userName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatWeekDisplay(entry.weekStart)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {entry.status === "submitted" ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ✓ 제출
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              임시저장
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {entry.status === "submitted" && (
                            <button
                              onClick={() => handleReject(entry.userId, entry.weekStart, entry.userName || entry.userId)}
                              className="px-3 py-1 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 transition-colors"
                            >
                              반려
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    이전
                  </button>
                  
                  <span className="px-4 py-2 text-sm text-gray-700">
                    {currentPage} / {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    다음
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
